<script setup lang="ts">
import { TAllColorType, TAllSizeType } from "@/package/global-types";
import { computed, PropType } from "vue";

defineOptions({
  name: "UiButton",
});

type ColorType =
  | "accent" // Старое значение (переделанное)
  | "line-gold" // Старое значение
  | "dark" // Старое значение
  | "primary"
  | "grey"
  | "white"
  | "static-dark"
  | "secondary"
  | "secondary-grey";
type SizeType = "l" | "m" | "m2" | "s";

const props = defineProps({
  color: {
    type: String as PropType<TAllColorType>,
    default: "accent",
  },

  label: {
    type: String,
    default: "",
  },

  type: {
    type: String,
    default: "button",
  },

  size: {
    type: String as PropType<TAllSizeType>,
    default: "m",
  },

  loading: {
    type: Boolean,
    default: false,
  },

  disabled: {
    type: Boolean,
    default: false,
  },

  fitContent: {
    type: Boolean,
    default: false,
  },

  href: {
    type: String,
    default: undefined,
  },

  to: {
    type: Object,
    default: null,
  },

  target: {
    type: String,
    default: "",
  },

  fullWidth: {
    type: Boolean,
    default: false,
  },
});

const currentTag = computed(() => {
  if (props.href && !props.to) {
    return "a";
  }

  if (!props.href && props.to) {
    return "router-link";
  }

  return "button";
});

const filteredProps = computed(() => {
  return {
    ...(currentTag.value === "button" && {
      type: props.type,
      disabled: props.disabled || props.loading,
    }),

    ...(currentTag.value !== "button" && {
      target: props.target,
    }),

    ...(currentTag.value === "router-link" && {
      to: props.to,
      target: props.target,
    }),

    ...(currentTag.value === "a" && {
      href: props.href,
    }),
  };
});
</script>

<template>
  <component
    :is="currentTag"
    class="ui-button"
    :class="[
      `ui-button_color_${color}`,
      `ui-button_size_${size}`,
      {
        'ui-button_fit-content': fitContent,
        'ui-button_loading': loading,
        'ui-button_full-width': fullWidth,
      },
    ]"
    v-bind="filteredProps"
  >
    <slot></slot>
    <span v-if="label" class="ui-button__label">{{ label }}</span>
    <span v-if="loading" class="ui-button__icon-loading icon-loader"></span>
  </component>
</template>

<style lang="scss">
.ui-button {
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  position: relative;
  white-space: nowrap;
  text-decoration: none;
  background: none;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
  width: fit-content;
  user-select: none;
  transition: all 0.15s ease-out;
  flex-shrink: 0;
  gap: 4px;

  &_loading {
    .ui-button__label {
      opacity: 0;
    }

    &:active {
      box-shadow: none;
    }

    > span svg {
      opacity: 0;
    }
  }

  &__icon-loading {
    background: var(--color-semantic-content-other-disable, #a8aaad);
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }

  &_color {
    &_accent {
      background: var(--color-semantic-content-normal-brand);
      color: var(--color-semantic-content-normal-static-dark);

      // // &:focus,
      &:hover {
        background: var(--color-semantic-background-hover-brand);
      }

      &:active {
        background: var(--color-semantic-background-active-brand);
      }

      &:disabled {
        background: var(--color-semantic-background-other-disable);
        color: var(--color-semantic-content-other-disable);
      }

      &.ui-button_loading {
        background: var(--color-semantic-content-normal-brand) !important;
      }

      .icon-loader {
        background: var(--color-semantic-content-normal-static-dark);
      }
    }

    &_primary {
      background: var(--color-semantic-background-normal-contrast);
      color: var(--color-semantic-content-normal-contrast-primary);

      // &:focus,
      &:hover {
        background: var(--color-semantic-background-hover-contrast);
      }

      &:active {
        background: var(--color-semantic-background-active-contrast);
      }

      &:disabled {
        background: var(--color-semantic-background-other-disable);
        color: var(--color-semantic-content-other-disable);
      }

      &.ui-button_loading {
        background: var(--color-semantic-background-normal-contrast) !important;
      }

      .icon-loader,
      .icon {
        background: var(--color-semantic-content-normal-contrast-primary);
      }
    }

    &_secondary {
      background: var(--color-semantic-background-normal-secondary);
      border: 1px solid var(--color-semantic-border-normal-soft);
      color: var(--color-semantic-content-normal-primary);

      // &:focus,
      &:hover {
        border: 1px solid var(--color-semantic-border-hover-soft);
        background: var(--color-semantic-background-hover-secondary);
      }

      &:active {
        border: 1px solid var(--color-semantic-border-active-soft);
        background: var(--color-semantic-background-active-secondary);
      }

      &:disabled {
        background: var(--color-semantic-background-other-disable);
        border: 1px solid var(--color-semantic-border-other-disable);
        color: var(--color-semantic-content-other-disable);
      }

      &.ui-button_loading {
        background: var(
          --color-semantic-background-normal-secondary
        ) !important;
        border: 1px solid var(--color-semantic-border-normal-soft) !important;
      }

      .icon-loader {
        background: var(--color-semantic-content-normal-primary);
      }
    }

    &_tertiary {
      background-color: var(--color-semantic-background-normal-primary);
      border: 1px solid var(--color-semantic-border-normal-soft);
      color: var(--color-semantic-content-normal-primary);

      // &:focus,
      &:hover {
        border: 1px solid var(--color-semantic-border-hover-soft);
      }

      &:active {
        border: 1px solid var(--color-semantic-border-active-soft);
      }

      &:disabled {
        border: 1px solid var(--color-semantic-border-other-disable);
        color: var(--color-semantic-content-other-disable);
      }

      &.ui-button_loading {
        background: transparent !important;
        border: 1px solid var(--color-semantic-border-normal-soft) !important;
      }

      .icon-loader {
        background: var(--color-semantic-content-normal-primary);
      }
    }

    // legacy values
    // Нужно будет удалить
    &_dark {
      border: 1px solid var(--color-semantic-border-normal-soft);
      background: var(--color-semantic-background-normal-secondary);
      color: var(--color-semantic-content-normal-contrast-primary);

      > span.icon {
        background: var(--text-dark-primary, #fff);
      }

      @media (hover: hover) {
        &:hover {
          border: 1px solid var(--text-dark-sixth, #bdbdbd);
        }

        &:disabled {
          cursor: not-allowed;
        }
      }
    }

    &_grey {
      background: var(--color-semantic-background-normal-secondary);
      color: var(--color-semantic-content-normal-primary);

      &:hover {
        background: var(--color-semantic-background-hover-secondary);
      }

      &:active {
        background: var(--color-semantic-background-active-secondary);
      }
    }

    &_white {
      background: var(--background-light-primary);
      color: var(--consistent-dark-primary);

      &:hover {
        background: var(--background-light-primary);
      }

      &:active {
        background: var(--background-light-fourth);
      }
    }

    &_static-dark {
      background: var(--color-semantic-background-normal-static-dark);
      color: var(--color-semantic-content-normal-static-light);
    }
  }

  &_size {
    &_l {
      @include body-M;
      height: 48px;
      padding: 12px 24px;

      @media (max-width: 767px) {
        height: 44px;
        padding: 12px 20px;
      }

      > span.icon {
        width: 16px;
        height: 16px;
      }

      .ui-button__icon-loading {
        width: 16px;
        height: 16px;
      }
    }

    &_m {
      @include body-S;
      height: 40px;
      padding: 10px 20px;

      @media (max-width: 767px) {
        height: 36px;
        padding: 9px 16px;
      }

      > span.icon {
        width: 16px;
        height: 16px;
      }

      .ui-button__icon-loading {
        width: 16px;
        height: 16px;
      }
    }

    &_m2 {
      @include body-3-local;
      height: 40px;
      padding: 10px 20px;

      > span.icon {
        width: 16px;
        height: 16px;
      }

      .ui-button__icon-loading {
        width: 16px;
        height: 16px;
      }
    }

    &_s {
      @include body-S;
      height: 36px;
      padding: 8px 16px;

      @media (max-width: 768px) {
        padding: 7px 12px;
        height: 32px;
      }

      > span.icon {
        width: 16px;
        height: 16px;
      }

      .ui-button__icon-loading {
        width: 16px;
        height: 16px;
      }
    }
  }

  &_full-width {
    width: 100%;
    min-width: 100%;
  }

  &_fit-content {
    word-wrap: break-word;
  }

  &:disabled {
    background: var(--color-semantic-background-other-disable);
    color: var(--color-semantic-content-other-disable);
    cursor: not-allowed !important;
  }
}
</style>
